import { FC, useEffect, useState, useRef, MouseEvent } from 'react'
import {
  Container,
  HoveredContainer
} from './styles'
import {
  OrangeButton,
  OrangeButtonSimpleText
} from 'src/components/portal/default'
import { FlexDiv } from '../index'
import { StepperProgressBar } from '../../../basic/Stepper/styles'
import StandardCheckbox from '../../../basic/StandardCheckbox'
import CircleActionButton from '../../../basic/CircleActionButton'
import { appListRowType, actionButtonsToRenderInHoveredRow } from 'src/constants/portal'
import { AppStatusFilterOptions, ApplicationListData } from 'src/dtos/portal'
import { FiSend } from 'react-icons/fi'
import { MdClose, MdDeleteOutline, MdOutlineDownload, MdRemoveRedEye } from 'react-icons/md'
import { TbMailForward, TbMail } from 'react-icons/tb'
import { isValid, format } from 'date-fns'
import Modal from 'react-modal'

interface AppListRowProps {
  rowType: AppStatusFilterOptions;
  rowData: ApplicationListData;
  checked: boolean;
  onClick: (checked: number) => void;
  openAppDrawer: () => void;
  submitApplication: (appId: number) => Promise<void>;
  sendReminder: (appIds: number[]) => Promise<void>;
  rejectApplication: (appIds: number[]) => Promise<void>;
  sendMagicLink: (appIds: number[]) => Promise<void>;
  deleteApplication: (appIds: number[]) => Promise<void>;
}

const AppListRow: FC<AppListRowProps> = ({ 
  rowType, 
  rowData, 
  checked, 
  onClick, 
  openAppDrawer, 
  submitApplication,
  sendReminder,
  rejectApplication,
  sendMagicLink,
  deleteApplication
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [buttonsDisabled, setButtonsDisabled] = useState(false)
  const [rejectAppModalOpen, setRejectAppModalOpen] = useState(false)
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  
  const renderData = (prop: keyof ApplicationListData) => {
    if(prop === 'percentageOfCompletion') {
      return <FlexDiv align='center' gap="12px">
          {rowData[prop]}%
            <StepperProgressBar willDisplay width={rowData[prop]} height="4px">
              <div/>
            </StepperProgressBar>
        </FlexDiv>
    } else if(prop === 'applicantType') {
      return <span style={{ textTransform: 'capitalize' }}>
        {rowData[prop] ? rowData[prop].toLowerCase() : 'N/A'}
      </span>
    } else if(prop === 'firmSize' || prop === 'applicantName' || prop === 'firmName') {
      return !rowData[prop] ? 'N/A' : rowData[prop]
    } else {
      return !rowData[prop] ? 'N/A' : isValid(new Date(rowData[prop])) ? format(new Date(rowData[prop]), 'MM/dd/yyyy') : rowData[prop]
    }
  }

  const handleCheckboxClick = () => {
    onClick(rowData.id)
  }

  const handleContainerClick = () => {
    if(buttonsDisabled === false)
    openAppDrawer()
  }

  useEffect(() => {
    if(!!containerRef && !!containerRef?.current) {
      containerRef.current.addEventListener('mouseenter', () => setIsHovered(true))
      containerRef.current.addEventListener('mouseleave', () => setIsHovered(false))
    }
  }, [containerRef])

  const handleSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setButtonsDisabled(true)
    await submitApplication(rowData.id)
    setButtonsDisabled(false)
  }

  const handleSendReminder = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setButtonsDisabled(true)
    await sendReminder([rowData.id])
    setButtonsDisabled(false)
  }

  const handleRejectApplication = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setButtonsDisabled(true)
    await rejectApplication([rowData.id])
    setButtonsDisabled(false)
  }

  const handleImpersonateApplication = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setButtonsDisabled(true)
    await sendMagicLink([rowData.id])
    setButtonsDisabled(false)
  }

  const handleDeleteApplication = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setButtonsDisabled(true)
    await deleteApplication([rowData.id])
    setButtonsDisabled(false)
  }

  return (
    <Container ref={containerRef} onClick={handleContainerClick}>
      <StandardCheckbox checked={checked} onChange={handleCheckboxClick} />
      <table>
        <thead>
          <tr>
            {appListRowType[rowType].columns.map(column => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {appListRowType[rowType].applicationProps.map(prop => (
              <td key={prop}>{renderData(prop)}</td>
            ))}
          </tr>
        </tbody>
      </table>
      <HoveredContainer className={`${isHovered ? 'hovered' : ''}`}>
        {actionButtonsToRenderInHoveredRow['SEND_REMINDER'].includes(rowType) ? (
          <CircleActionButton
            label="Send Reminder"
            bg="#EFEFEF"
            disabled={buttonsDisabled}
            onClick={handleSendReminder}
          >
            <FiSend/>
          </CircleActionButton>
        ) : null}
        {/* {actionButtonsToRenderInHoveredRow['SUBMIT'].includes(rowType) ? (
          <CircleActionButton
            label="Submit"
            bg="#EFEFEF"
            disabled={buttonsDisabled}
            onClick={handleSubmit}
          >
            <TbMailForward/> 
          </CircleActionButton>
        ) : null} */}
        {actionButtonsToRenderInHoveredRow['DOWNLOAD'].includes(rowType) ? (
          <CircleActionButton
            label="Download"
            bg="#EFEFEF"
            disabled={buttonsDisabled}
          >
            <MdOutlineDownload/>
          </CircleActionButton>
        ) : null}
        {actionButtonsToRenderInHoveredRow['CONTACT'].includes(rowType) ? (
          <a href={`mailto:${rowData.applicantEmail}`} style={{ textDecoration: 'none' }}>
            <CircleActionButton
              label="Contact"
              bg="#EFEFEF"
              disabled={buttonsDisabled}
              onClick={e => e.stopPropagation()}
            >
              <TbMail/>
            </CircleActionButton>
          </a>
        ) : null}
        {actionButtonsToRenderInHoveredRow['REJECT'].includes(rowType) ? (
          <CircleActionButton
            label="Reject"
            bg="#EFEFEF"
            disabled={buttonsDisabled}
            onClick={() => setRejectAppModalOpen(true)}
          >
            <MdClose/>
          </CircleActionButton>
        ) : null}
        {actionButtonsToRenderInHoveredRow['IMPERSONATE'].includes(rowType) ? (
          <CircleActionButton
            label="Impersonate"
            bg="#EFEFEF"
            disabled={buttonsDisabled}
            onClick={() => setImpersonateModalOpen(true)}
          >
            <MdRemoveRedEye/>
          </CircleActionButton>
        ) : null}
        {actionButtonsToRenderInHoveredRow['DELETE'].includes(rowType) ? (
          <CircleActionButton
            label="Delete"
            bg="#EFEFEF"
            disabled={buttonsDisabled}
            onClick={handleDeleteApplication}
          >
            <MdDeleteOutline/>
          </CircleActionButton>
        ) : null}
        
      </HoveredContainer>
      <Modal
        isOpen={rejectAppModalOpen}
        onRequestClose={(e) => {
          e.stopPropagation()
          setRejectAppModalOpen(false)
        }}
        overlayClassName="react-modal-overlay"
        className="react-modal-content"
      >
        <FlexDiv dir='column' gap="16px">
          <strong>Are you sure you want to reject the selected application?</strong>
          <span>Once an application is rejected, you will not be able to submit it to ISI.</span>
          <FlexDiv dir="column" gap="24px" margin="48px 0 0 0" align='flex-end'>
            <OrangeButton
              onClick={handleRejectApplication}
              disabled={buttonsDisabled}
            >
              Reject application
            </OrangeButton>
            <OrangeButtonSimpleText
              onClick={(e) => {
                e.stopPropagation()
                setRejectAppModalOpen(false)
              }}
              disabled={buttonsDisabled}
            >
              Cancel
            </OrangeButtonSimpleText>
          </FlexDiv>
        </FlexDiv>
      </Modal>

{/* impersonate */}

      <Modal
        isOpen={impersonateModalOpen}
        onRequestClose={(e) => {
          e.stopPropagation()
          setImpersonateModalOpen(false)
        }}
        overlayClassName="react-modal-overlay"
        className="react-modal-content"
      >
        <FlexDiv dir='column' gap="16px">
          <strong>Are you sure you want to impersonate the user of the selected application?</strong>
          <FlexDiv dir="column" gap="24px" margin="48px 0 0 0" align='flex-end'>
            <OrangeButton
              onClick={handleImpersonateApplication}
              disabled={buttonsDisabled}
            >
              Impersonate
            </OrangeButton>
            <OrangeButtonSimpleText
              onClick={(e) => {
                e.stopPropagation()
                setImpersonateModalOpen(false)
              }}
              disabled={buttonsDisabled}
            >
              Cancel
            </OrangeButtonSimpleText>
          </FlexDiv>
        </FlexDiv>
      </Modal>
    </Container>
  )
}

export default AppListRow